import { FC, useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Button } from '../button/button.tsx';
import { ZoomInIcon } from '../icons/react/zoom-in.tsx';
import { ZoomOutIcon } from '../icons/react/zoom-out.tsx';
import { Slider } from '../slider/slider.tsx';
import {
  avatarEditorContainer,
  avatarEditorSlider,
  sliderContainer,
} from './image-editor.css.ts';
import { getCroppedImg } from './utils.ts';

export interface ImageEditorProps {
  image: File;
  onSubmit: (file: File) => void;
  width?: number;
  height?: number;
  borderRadius?: number;
  cropperPadding?: number;
}

export const ImageEditor: FC<ImageEditorProps> = ({
  onSubmit,
  image,
  width = 350,
  height = 350,
  borderRadius = 9999,
  cropperPadding = 0,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [imageURL, setImageURL] = useState<string>();

  const maxZoom = 2;

  useEffect(() => {
    if (image) {
      const url = URL.createObjectURL(image);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setImageURL(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [image]);

  const handleCropImage = async () => {
    try {
      if (imageURL && croppedAreaPixels) {
        const croppedImageFile = await getCroppedImg(
          imageURL,
          croppedAreaPixels,
        );
        onSubmit(croppedImageFile as File);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return imageURL ? (
    <div className={avatarEditorContainer}>
      <div>
        <Button onClick={handleCropImage}>Apply</Button>
      </div>

      <div style={{ position: 'relative', width: width, height: height }}>
        <Cropper
          image={imageURL}
          crop={crop}
          zoom={zoom}
          aspect={width / height}
          onCropChange={setCrop}
          onCropComplete={(_, croppedAreaPixels) =>
            setCroppedAreaPixels(croppedAreaPixels)
          }
          onZoomChange={setZoom}
          showGrid={false}
          style={{
            cropAreaStyle: {
              borderRadius: borderRadius,
              border: 0,
              color: 'rgba(256, 256, 256, 0.5)',
            },
            containerStyle: {
              background: 'rgb(205, 205, 205)',
            },
          }}
          cropSize={{
            width: width - cropperPadding,
            height: height - cropperPadding,
          }}
          objectFit={'cover'}
          maxZoom={maxZoom}
        />
      </div>

      <div className={sliderContainer}>
        <ZoomOutIcon />
        <Slider
          containerClassName={avatarEditorSlider}
          min={1}
          max={maxZoom}
          step={0.01}
          value={[zoom]}
          onValueChange={([v]) => setZoom(v)}
          withLabels={false}
        />
        <ZoomInIcon />
      </div>
    </div>
  ) : null;
};
